// ═══════════════════════════════════════════════════════════════════
//  TOQUM · PO DOCUMENT HUB
//  Everything a QA needs when an inspection is scheduled, on one page:
//  techpack, PO sheet, spec sheet, lab test reports, packing list,
//  production report up to the last entry — plus a gallery of every
//  image captured at any sample, inspection or other process.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, num, today, Page, Section, Grid, Btn, DataTable, Tag, Badge, StatCard, CardRow, Split,
  Collapse, Modal, Field, printHTML, uploadFile,
} from './Shared';

const DOC_GROUPS = [
  { key:'techpack', label:'Techpack',        icon:'📐', color:'#7c3aed',
    match: t => /techpack|bom/i.test(t) },
  { key:'po',       label:'PO Sheet',        icon:'📋', color:'#0073ea',
    match: t => /^po sheet|purchase order/i.test(t) },
  { key:'spec',     label:'Spec Sheet',      icon:'📄', color:'#00a65e',
    match: t => /spec|measurement/i.test(t) },
  { key:'lab',      label:'Lab Test Report', icon:'🧪', color:'#0da99b',
    match: t => /lab|test report|ort|tp/i.test(t) },
  { key:'packing',  label:'Packing List',    icon:'📦', color:'#fdab3d',
    match: t => /packing|invoice/i.test(t) },
  { key:'inspection',label:'Inspection Report', icon:'✅', color:'#00c875',
    match: t => /inspection|report/i.test(t) },
  { key:'sample',   label:'Sample Report',   icon:'🧵', color:'#a25ddc',
    match: t => /sample/i.test(t) },
  { key:'other',    label:'Other',           icon:'📎', color:'#676879', match: () => true },
];

const groupOf = (docType='') => DOC_GROUPS.find(g => g.match(docType)) || DOC_GROUPS[DOC_GROUPS.length-1];

export default function DocumentHub({ db, setDb, saveToDB, currentUser, poNo, onClose, embedded }) {
  const [lightbox, setLightbox] = useState(null);
  const [filter, setFilter] = useState('All');

  const po    = (db.pos||[]).find(p => p.poNo === poNo);
  const style = (db.styles||[]).find(s => s.styleNo === po?.styleNo);

  // ── Gather every artefact attached to this PO from every module ──
  const docs = useMemo(() => {
    const out = [];
    const push = (o) => out.push(o);

    (db.poDocuments||[]).filter(d=>d.poNo===poNo).forEach(d => push({
      id:d.id, source:'PO Documents', docType:d.type, name:d.name,
      url:d.data || d.url, isImage:d.isImage, date:d.date, by:d.uploadedBy, caption:'',
    }));

    // Inspection images + the report itself
    (db.inspections||[]).filter(i=>i.poNo===poNo).forEach(i => {
      push({ id:`insp-${i.id}`, source:'Inspection', docType:'Inspection Report',
        name:`${i.stage} — ${i.result||i.status}`, url:null, isImage:false,
        date:i.date, by:i.qaMember, record:i });
      (i.imagesData||[]).forEach((im,ix)=>push({ id:`insp-${i.id}-${ix}`, source:`Inspection — ${i.stage}`,
        docType:'Inspection Image', name:im.name, url:im.data||im.url, isImage:true,
        date:i.date, by:i.qaMember, caption:im.caption }));
    });

    // Sample evaluations
    (db.sampleEvaluations||[]).filter(s=>s.poNo===poNo).forEach(s => {
      if (s.reportFile) push({ id:`sr-${s.id}`, source:`Sample — ${s.stage}`, docType:'Sample Report',
        name:s.reportFile.name, url:s.reportFile.url, isImage:false, date:s.date, by:s.submittedBy });
      (s.images||[]).forEach((im,ix)=>push({ id:`si-${s.id}-${ix}`, source:`Sample — ${s.stage}`,
        docType:'Sample Image', name:im.name, url:im.url, isImage:true,
        date:s.date, by:s.submittedBy, caption:im.caption }));
    });

    // Customer complaint evidence tied to this PO
    (db.customerComplaints||[]).filter(c=>c.poNo===poNo).forEach(c => {
      (c.images||[]).forEach((im,ix)=>push({ id:`cc-${c.id}-${ix}`, source:'Customer Complaint',
        docType:'Complaint Image', name:im.name, url:im.url, isImage:true,
        date:c.date, by:c.loggedBy, caption:im.caption }));
      (c.documents||[]).forEach((d,ix)=>push({ id:`ccd-${c.id}-${ix}`, source:'Customer Complaint',
        docType:'Complaint Document', name:d.name, url:d.url, isImage:false, date:c.date, by:c.loggedBy }));
    });

    // Claim proofs
    (db.claims||[]).filter(c=>c.poRef===poNo).forEach(c => {
      (c.proofs||[]).forEach((p,ix)=>push({ id:`cl-${c.id}-${ix}`, source:'Supplier Claim',
        docType: p.type==='video' ? 'Claim Video' : 'Claim Image', name:p.name,
        url:p.data, isImage:p.type!=='video', date:c.raisedOn, by:c.complainant }));
    });

    return out.sort((a,b)=>(b.date||'').localeCompare(a.date||''));
  }, [db, poNo]);

  const images = docs.filter(d=>d.isImage && d.url);

  // ── Generate printable HTML report from stored JSON record ──────
  const printReport = (rec, docType) => {
    const h = rec.header || {};
    const fmt = rec.format || 'INSP';
    const po  = (db.pos||[]).find(p=>p.poNo===rec.poNo) || {};

    // Measurement table HTML
    const measHtml = (rec.measRows||[]).length ? `
      <h3>Measurement Spec Sheet</h3>
      <table>
        <tr style="background:#1f1f1f;color:white">
          <th>Measurement Point</th><th>Tol−</th><th>Tol+</th>
          ${(rec.measSizes||[]).map(s=>`<th>${s}<br/><small>spec · actual</small></th>`).join('')}
          <th>Result</th>
        </tr>
        ${(rec.measRows||[]).map(r=>`
          <tr style="background:${r.result==='Fail'?'#fdeaec':r.result==='Pass'?'#f0fdf4':'white'}">
            <td><b>${r.point}</b></td>
            <td style="text-align:center">${r.tolMinus||r.tolerance||'—'}</td>
            <td style="text-align:center">${r.tolPlus||r.tolerance||'—'}</td>
            ${(rec.measSizes||[]).map(s=>{
              const spec = r.specPerSize?.[s]||r.spec||'';
              const act  = r.actuals?.[s]||'';
              const specN = parseFloat(spec), actN = parseFloat(act);
              const tol   = parseFloat(r.tolPlus||r.tolerance||1);
              const bad   = !isNaN(actN)&&!isNaN(specN)&&Math.abs(actN-specN)>tol;
              return `<td style="text-align:center;${bad?'background:#fdeaec;color:#b4232f;font-weight:700':''}">
                ${spec?`<small style="color:#0073ea">${spec}</small><br/>`:''}${act||'—'}
                ${!isNaN(actN)&&!isNaN(specN)?`<br/><small style="color:${bad?'#b4232f':'#0a7d43'}">${actN-specN>0?'+':''}${(actN-specN).toFixed(1)}</small>`:''}
              </td>`;
            }).join('')}
            <td style="font-weight:800;color:${r.result==='Fail'?'#b4232f':r.result==='Pass'?'#0a7d43':'#676879'}">${r.result||'—'}</td>
          </tr>`).join('')}
        <tr style="background:#f1f5f9;font-weight:800">
          <td colspan="${2+(rec.measSizes||[]).length}">Summary</td>
          <td>${(rec.measSummary?.pass||0)} Pass · <span style="color:#b4232f">${(rec.measSummary?.fail||0)} Fail</span> of ${(rec.measSummary?.total||0)}</td>
        </tr>
      </table>` : '';

    // Defects table
    const defHtml = (rec.defects||[]).length ? `
      <h3>Defect Record</h3>
      <table>
        <tr style="background:#1f1f1f;color:white"><th>#</th><th>Defect</th><th>Classification</th><th>Location</th><th>Qty</th></tr>
        ${(rec.defects||[]).map((d,i)=>`<tr><td>${i+1}</td><td>${d.defect||d.description||'—'}</td>
          <td style="color:${d.classification==='Critical'?'#b4232f':d.classification==='Major'?'#8a5a00':'#676879'};font-weight:700">${d.classification||'—'}</td>
          <td>${d.location||'—'}</td><td><b>${d.qty||0}</b></td></tr>`).join('')}
        <tr style="background:#f1f5f9;font-weight:800">
          <td colspan="4">TOTAL DEFECTS</td>
          <td>${(rec.defects||[]).reduce((a,d)=>a+(parseInt(d.qty)||0),0)}</td>
        </tr>
      </table>` : '';

    // Inspection images
    const imgHtml = (rec.imagesData||[]).length ? `
      <h3>Inspection Images</h3>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px">
        ${(rec.imagesData||[]).map(im=>`
          <div>
            <img src="${im.data||im.url||''}" style="width:100%;height:160px;object-fit:cover;border-radius:6px;border:1px solid #e6e9ef"/>
            <div style="font-size:9px;color:#676879;margin-top:3px">${im.caption||im.name||''}</div>
          </div>`).join('')}
      </div>` : '';

    // Styling checks
    const styleHtml = rec.styling ? `
      <h3>Styling Check</h3>
      <table>
        <tr style="background:#f1f5f9"><th>Check</th><th>Result</th></tr>
        ${Object.entries(rec.styling).map(([k,v])=>`<tr><td>${k}</td>
          <td style="font-weight:700;color:${v==='NG'?'#b4232f':v==='OK'?'#0a7d43':'#676879'}">${v}</td></tr>`).join('')}
      </table>` : '';

    // AQL plan
    const aqlHtml = rec.aql ? `
      <h3>AQL Sampling Plan</h3>
      <table>
        <tr><td><b>Visual AQL:</b></td><td>${rec.aql.visual||'—'}</td>
            <td><b>Measurement AQL:</b></td><td>${rec.aql.measurement||'—'}</td></tr>
        ${rec.aql.planV?`<tr><td><b>Visual n / Ac / Re:</b></td><td>${rec.aql.planV?.n} / ${rec.aql.planV?.ac} / ${rec.aql.planV?.re}</td>
          <td><b>Measurement n / Ac / Re:</b></td><td>${rec.aql.planM?.n||'—'} / ${rec.aql.planM?.ac||'—'} / ${rec.aql.planM?.re||'—'}</td></tr>`:''}
      </table>` : '';

    const title = `${fmt} — ${rec.stage||''} — PO ${rec.poNo} — ${rec.supplier}`;

    printHTML(title, `
      <style>
        body{font-family:-apple-system,Arial,sans-serif;font-size:11px;color:#1f1f1f}
        h2{font-size:15px;margin:0 0 2px;color:#1f1f1f}
        h3{font-size:11px;margin:16px 0 6px;text-transform:uppercase;letter-spacing:.06em;
           color:#0073ea;border-bottom:2px solid #0073ea;padding-bottom:3px}
        table{border-collapse:collapse;width:100%;margin-bottom:12px;font-size:10.5px}
        th,td{border:1px solid #e6e9ef;padding:5px 8px;text-align:left}
        th{background:#f1f5f9;font-weight:700}
        .kv{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:12px}
        .kv div{background:#f8fafc;border-radius:4px;padding:6px 8px}
        .kv .l{font-size:8.5px;text-transform:uppercase;color:#676879;letter-spacing:.05em}
        .kv .v{font-size:13px;font-weight:900;color:#1f1f1f;margin-top:2px}
        .result{display:inline-block;padding:4px 14px;border-radius:10px;font-weight:900;font-size:13px;
          background:${rec.result==='Passed'?'#e9f9f0':rec.result==='Failed'?'#fdeaec':'#fff8e6'};
          color:${rec.result==='Passed'?'#0a7d43':rec.result==='Failed'?'#b4232f':'#8a5a00'}}
        .sign-table td{height:50px;width:25%;font-size:10px;font-weight:700;color:#676879}
        @media print{@page{margin:15mm 12mm}}
      </style>

      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1f1f1f;padding-bottom:10px;margin-bottom:14px">
        <div>
          <div style="font-size:9px;color:#676879;text-transform:uppercase;letter-spacing:.08em;margin-bottom:3px">
            TOQUM · Quality Management System
          </div>
          <h2>${title}</h2>
          <div style="font-size:10px;color:#676879;margin-top:2px">
            Date: ${rec.date||'—'} &nbsp;·&nbsp; QA: ${rec.qaMember||h.technician||'—'}
            &nbsp;·&nbsp; Stage: ${rec.stage||h.userType||'—'}
          </div>
        </div>
        <div style="text-align:right">
          <div class="result">${rec.result||rec.status||'—'}</div>
          <div style="font-size:9px;color:#676879;margin-top:4px">Format: ${fmt}</div>
        </div>
      </div>

      <!-- PO + Style details -->
      <div class="kv">
        <div><div class="l">PO Number</div><div class="v">${rec.poNo||'—'}</div></div>
        <div><div class="l">Style No</div><div class="v">${rec.styleNo||'—'}</div></div>
        <div><div class="l">Supplier</div><div class="v">${rec.supplier||'—'}</div></div>
        <div><div class="l">Brand</div><div class="v">${rec.brand||'—'}</div></div>
        <div><div class="l">Order Qty</div><div class="v">${h.ordQty?Number(h.ordQty).toLocaleString():'—'}</div></div>
        <div><div class="l">Colour</div><div class="v">${h.colour||'—'}</div></div>
        <div><div class="l">Country</div><div class="v">${h.country||'—'}</div></div>
        <div><div class="l">TOD</div><div class="v">${h.tod||'—'}</div></div>
        <div><div class="l">Section</div><div class="v">${h.section||'—'}</div></div>
        <div><div class="l">Unit / Line</div><div class="v">${h.unit||'—'} / ${h.line||'—'}</div></div>
        <div><div class="l">Cut Qty</div><div class="v">${h.cutQty?Number(h.cutQty).toLocaleString():'—'}</div></div>
        <div><div class="l">Packing Qty</div><div class="v">${h.packQty?Number(h.packQty).toLocaleString():'—'}</div></div>
        <div><div class="l">Visual Check Qty</div><div class="v">${h.visualCheckQty?Number(h.visualCheckQty).toLocaleString():'—'}</div></div>
        <div><div class="l">Meas Check Qty</div><div class="v">${h.measurementQty?Number(h.measurementQty).toLocaleString():'—'}</div></div>
        <div><div class="l">Critical</div><div class="v" style="color:${(rec.totals?.critical||0)>0?'#b4232f':'inherit'}">${rec.totals?.critical||0}</div></div>
        <div><div class="l">Major</div><div class="v" style="color:${(rec.totals?.major||0)>0?'#8a5a00':'inherit'}">${rec.totals?.major||0}</div></div>
        <div><div class="l">Minor</div><div class="v">${rec.totals?.minor||0}</div></div>
        <div><div class="l">Total Defects</div><div class="v">${(rec.totals?.critical||0)+(rec.totals?.major||0)+(rec.totals?.minor||0)}</div></div>
      </div>

      ${aqlHtml}
      ${styleHtml}
      ${defHtml}
      ${measHtml}
      ${rec.comments?`<h3>Comments</h3><p>${rec.comments}</p>`:''}
      ${imgHtml}

      <!-- Signatures -->
      <h3>Signatures</h3>
      <table class="sign-table">
        <tr>
          <th>Prepared By</th><th>Production Manager</th><th>Quality Manager</th><th>AGM / GM (Q.A)</th>
        </tr>
        <tr>
          <td>${rec.qaMember||h.technician||'—'}</td><td></td><td></td><td></td>
        </tr>
      </table>

      <p style="font-size:9px;color:#676879;margin-top:16px;border-top:1px solid #e6e9ef;padding-top:8px">
        Note: Inspection of the order does not relieve the factory from responsibility for the quality of shipped goods.
        Generated by Toqum QMS · ${new Date().toLocaleString()}
      </p>
    `);
  };
  const shown  = filter==='All' ? docs
    : filter==='Images' ? images
    : docs.filter(d=>groupOf(d.docType).key===filter);

  // Production report up to the latest entry
  const production = useMemo(() => {
    const logs = (db.productionLogs||[]).filter(l=>l.poNo===poNo);
    const stages = ['Cutting','Sewing','Washing','Finishing','Packing'];
    const last = logs.map(l=>l.date).sort().slice(-1)[0];
    return {
      lastDate: last,
      entries: logs.length,
      byStage: stages.map(st=>{
        const till = logs.filter(l=>l.stage===st).reduce((a,l)=>a+num(l.qty),0);
        return { id:st, stage:st, till, balance:num(po?.qty)-till,
          pct: num(po?.qty) ? Math.round(till/num(po.qty)*100) : 0 };
      }),
    };
  }, [db.productionLogs, poNo, po]);

  const plan = (db.qualityPlans||[]).find(q=>q.poNo===poNo);
  const checklist = (db.followUpChecklists||[]).find(c=>c.poNo===poNo);

  const printPack = () => printHTML(`Inspection Pack — PO ${poNo}`, `
    <h1>TOQUM · Inspection Document Pack</h1>
    <table><tr><td><b>PO:</b> ${poNo}</td><td><b>Style:</b> ${po?.styleNo||''}</td>
      <td><b>Supplier:</b> ${po?.supplier||''}</td><td><b>Buyer:</b> ${po?.brand||''}</td></tr>
      <tr><td><b>PO Qty:</b> ${num(po?.qty).toLocaleString()}</td>
      <td><b>Ex-Factory:</b> ${po?.exFactory||''}</td>
      <td colspan="2"><b>Printed:</b> ${today()} by ${currentUser.name}</td></tr></table>
    <h3>Production Status ${production.lastDate ? `(to ${production.lastDate})` : ''}</h3>
    <table><tr><th>Stage</th><th>Completed</th><th>Balance</th><th>%</th></tr>
      ${production.byStage.map(s=>`<tr><td>${s.stage}</td><td>${s.till.toLocaleString()}</td>
        <td>${s.balance.toLocaleString()}</td><td>${s.pct}%</td></tr>`).join('')}</table>
    <h3>Document Index</h3>
    <table><tr><th>Type</th><th>Name</th><th>Source</th><th>Date</th><th>By</th></tr>
      ${docs.map(d=>`<tr><td>${d.docType}</td><td>${d.name}</td><td>${d.source}</td>
        <td>${d.date||''}</td><td>${d.by||''}</td></tr>`).join('')}</table>
    ${checklist ? `<h3>Follow-up Checklist (${checklist.items.length} points)</h3>
      <table><tr><th>Source</th><th>Point</th><th>Check At</th><th>Status</th></tr>
      ${checklist.items.map(i=>`<tr><td>${i.source}</td><td>${i.point}</td>
        <td>${i.checkAt||''}</td><td>${i.status||''}</td></tr>`).join('')}</table>` : ''}
    ${images.length ? `<h3>Image Gallery (${images.length})</h3><div class="imgs">
      ${images.map(i=>`<div><img src="${i.url}"/><div style="font-size:8px">${i.source} · ${i.caption||i.name}</div></div>`).join('')}
      </div>` : ''}`);

  const body = (
    <div>
      <CardRow min={180}>
        <StatCard label="Documents" value={docs.length} color={T.primary} />
        <StatCard label="Images" value={images.length} color={T.teal} />
        <StatCard label="PO Qty" value={num(po?.qty).toLocaleString()} color={T.purple} />
        <StatCard label="Production Entries" value={production.entries}
          sub={production.lastDate ? `to ${production.lastDate}` : 'none yet'} color={T.accent} />
        <StatCard label="Plan Steps Done"
          value={plan ? `${plan.steps.filter(s=>['Passed','Approved'].includes(s.status)).length}/${plan.steps.length}` : '—'}
          color={T.success} />
        <StatCard label="Checklist Points" value={checklist ? checklist.items.length : 0}
          sub={checklist ? `${checklist.items.filter(i=>i.status==='Verified').length} verified` : 'none built'}
          color={checklist ? T.warning : T.textSub} />
      </CardRow>

      <Section title={`Production Report ${production.lastDate ? `— up to ${production.lastDate}` : '— no entries yet'}`}>
        <DataTable columns={['Stage','Completed','Balance','Progress']} rows={production.byStage}
          renderCell={(r,c)=>({
            Stage:<b>{r.stage}</b>, Completed:r.till.toLocaleString(),
            Balance:<b style={{ color:r.balance<=0?T.success:T.textSub }}>{r.balance.toLocaleString()}</b>,
            Progress:<span style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:120, height:7, background:'#f1f5f9', borderRadius:4, display:'inline-block' }}>
                <span style={{ display:'block', height:7, width:`${Math.min(100,r.pct)}%`, borderRadius:4,
                  background: r.pct>=100?T.success:r.pct>=50?T.primary:T.warning }} /></span>
              <b>{r.pct}%</b></span>,
          }[c])} />
      </Section>

      <Section title="Documents"
        actions={<span style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          {['All','Images',...DOC_GROUPS.map(g=>g.key)].map(k=>{
            const g = DOC_GROUPS.find(x=>x.key===k);
            const count = k==='All' ? docs.length : k==='Images' ? images.length
              : docs.filter(d=>groupOf(d.docType).key===k).length;
            return (
              <button key={k} onClick={()=>setFilter(k)}
                style={{ padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:10.5, fontWeight:700,
                  border:`1px solid ${filter===k ? (g?.color||T.textMain) : T.border}`,
                  background: filter===k ? (g?.color||T.textMain) : 'white',
                  color: filter===k ? 'white' : T.textSub }}>
                {g ? `${g.icon} ${g.label}` : k} ({count})
              </button>
            );
          })}
        </span>}>
        {shown.length===0 ? (
          <div style={{ padding:24, textAlign:'center', color:T.textSub, fontSize:12.5 }}>
            Nothing here yet for this filter. Documents appear automatically as they are uploaded in
            Style, QA, Sample Evaluation and Customer Feedback.
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(165px,1fr))', gap:12 }}>
            {shown.map(d=>{
              const g = groupOf(d.docType);
              return (
                <div key={d.id}
                  onClick={()=> {
                if (d.isImage && d.url) { setLightbox(d); return; }
                if (d.url) { window.open(d.url,'_blank'); return; }
                if (d.record) { printReport(d.record, d.docType); return; }
              }}
                  style={{ border:`1px solid ${T.border}`, borderRadius:9, overflow:'hidden',
                    background:'white', cursor: (d.url || d.record) ? 'pointer' : 'default' }}>
                  {d.isImage && d.url
                    ? <img src={d.url} alt={d.name} style={{ width:'100%', height:115, objectFit:'cover' }} />
                    : <div style={{ height:115, display:'flex', flexDirection:'column',
                        alignItems:'center', justifyContent:'center',
                        fontSize:32, background:`${g.color}10`, color:g.color }}>
                        {g.icon}
                        {d.record && <span style={{ fontSize:9.5, marginTop:5, fontWeight:700,
                          color:T.primary, textAlign:'center', lineHeight:1.3 }}>
                          Click to Preview<br/>& Print
                        </span>}
                      </div>}
                  <div style={{ padding:8, fontSize:10.5, lineHeight:1.5 }}>
                    <Tag label={d.docType} color={g.color} />
                    <div style={{ fontWeight:700, marginTop:3, wordBreak:'break-word' }}>
                      {d.caption || d.name}</div>
                    <div style={{ color:T.textSub }}>{d.source}</div>
                    <div style={{ color:T.textSub }}>{d.date} · {d.by||'—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {checklist && (
        <Section title={`Follow-up Checklist — ${checklist.items.length} points to verify during this inspection`}>
          <DataTable columns={['Source','Area','Point to Check','Detail','Severity','Check At','Status']}
            rows={checklist.items.map((it,i)=>({ ...it, id:i }))}
            renderCell={(r,c)=>({
              Source:<Tag label={r.source} color={r.source.includes('Risk')?T.purple:T.teal} />,
              Area:r.area, 'Point to Check':<b>{r.point}</b>, Detail:r.detail,
              Severity:<Badge status={r.severity==='Critical'?'Failed':'Pending'} />,
              'Check At':r.checkAt,
              Status:<Badge status={r.status==='Verified'?'Passed':'Open'} />,
            }[c])} />
        </Section>
      )}
    </div>
  );

  const header = (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
      gap:12, flexWrap:'wrap', marginBottom:14 }}>
      <div>
        <div style={{ fontSize:16, fontWeight:900 }}>📁 Document Hub — PO {poNo}</div>
        <div style={{ fontSize:12, color:T.textSub }}>
          {po?.styleNo} · {po?.supplier} · {po?.brand} · {num(po?.qty).toLocaleString()} pcs
          {style?.description ? ` · ${style.description}` : ''}
        </div>
      </div>
      <div style={{ display:'flex', gap:6 }}>
        <Btn small outline label="🖨 Print Inspection Pack" onClick={printPack} />
        {onClose && <Btn small color="#64748b" label="← Back" onClick={onClose} />}
      </div>
    </div>
  );

  if (!po) return (
    <Section title="Document Hub">
      <span style={{ fontSize:12, color:T.textSub }}>PO {poNo} not found.</span>
    </Section>
  );

  return (
    <div>
      {header}
      {body}
      {lightbox && (
        <Modal title={lightbox.caption || lightbox.name} width={880} onClose={()=>setLightbox(null)}
          actions={<Btn label="Open Full Size" outline onClick={()=>window.open(lightbox.url,'_blank')} />}>
          <img src={lightbox.url} alt={lightbox.name}
            style={{ width:'100%', borderRadius:8, border:`1px solid ${T.border}` }} />
          <div style={{ fontSize:12, color:T.textSub, marginTop:8 }}>
            {lightbox.source} · {lightbox.date} · uploaded by {lightbox.by||'—'}
          </div>
        </Modal>
      )}
    </div>
  );
}
